<template>
  <div class="update-package-container">
    <Card>
      <p slot="title">更新包管理</p>

      <div class="search-con">
        <Input
          v-model="searchParams.keyword"
          placeholder="请输入名称/版本/包名搜索"
          style="width: 300px; margin-right: 10px"
          clearable
          @on-enter="handleSearch"
        >
          <span slot="prepend">搜索</span>
        </Input>
        <Button type="primary" @click="handleSearch">搜索</Button>
        <Button type="success" @click="handleAdd" style="margin-left: 10px">新增更新包</Button>
      </div>

      <div style="margin-top: 20px">
        <Table
          :columns="columns"
          :data="tableData"
          :loading="loading"
          border
          stripe
        >
          <template slot-scope="{ row }" slot="status">
            <Tag :color="row.status === 1 ? 'green' : 'red'">
              {{ row.status === 1 ? '启用' : '禁用' }}
            </Tag>
          </template>

          <template slot-scope="{ row }" slot="file_size">
            {{ formatFileSize(row.file_size) }}
          </template>

          <template slot-scope="{ row }" slot="action">
            <Button type="primary" size="small" @click="handleEdit(row)">编辑</Button>
            <Button
              type="error"
              size="small"
              style="margin-left: 6px"
              @click="handleDelete(row)"
            >删除</Button>
            <Button
              type="warning"
              size="small"
              style="margin-left: 6px"
              @click="handleBatchUpdate(row)"
            >批量更新</Button>
          </template>
        </Table>

        <div style="margin-top: 20px; text-align: right">
          <Page
            :total="total"
            :current="pageParams.page"
            :page-size="pageParams.limit"
            @on-change="handlePageChange"
            @on-page-size-change="handlePageSizeChange"
            show-sizer
            show-total
          />
        </div>
      </div>
    </Card>

    <!-- 新增/编辑弹窗 -->
    <Modal
      v-model="modalVisible"
      :title="modalTitle"
      width="650px"
      @on-cancel="handleCancel"
    >
      <Form ref="packageForm" :model="formData" :rules="formRules" :label-width="100">
        <FormItem label="名称" prop="name">
          <Input v-model="formData.name" placeholder="请输入更新包名称" />
        </FormItem>

        <FormItem label="版本" prop="version">
          <Input v-model="formData.version" placeholder="请输入版本号，如 1.0.0" />
        </FormItem>

        <FormItem label="设备类型" prop="device_type">
          <Select
            v-model="formData.device_type"
            placeholder="请选择设备类型"
            clearable
          >
            <Option v-for="item in deviceTypeOptions" :key="item.value" :value="item.value">{{ item.label }}</Option>
          </Select>
        </FormItem>

        <FormItem label="状态" prop="status">
          <i-switch
            v-model="formData.status"
            :true-value="1"
            :false-value="0"
          >
            <span slot="open">启用</span>
            <span slot="close">禁用</span>
          </i-switch>
        </FormItem>

        <FormItem label="包名" prop="package_name">
          <Input v-model="formData.package_name" placeholder="请输入包名，如 com.example.app" />
        </FormItem>

        <FormItem label="上传文件">
          <Upload
            :before-upload="handleBeforeUpload"
            :show-upload-list="false"
            accept=".apk"
            action=""
          >
            <Button icon="ios-cloud-upload-outline" :loading="uploadLoading">
              {{ uploadLoading ? '上传中...' : '选择APK文件' }}
            </Button>
          </Upload>
          <div v-if="formData.file_url" style="margin-top: 8px; color: #19be6b;">
            <Icon type="ios-checkmark-circle" />
            已上传: {{ formData.package_name || '文件' }}
            <span style="color: #808695; margin-left: 8px;">
              {{ formData.file_size ? formatFileSize(formData.file_size) : '' }}
            </span>
          </div>
        </FormItem>

        <FormItem label="文件地址" prop="file_url">
          <Input v-model="formData.file_url" placeholder="上传文件后自动填充" readonly />
        </FormItem>

        <FormItem label="备注" prop="remark">
          <Input
            v-model="formData.remark"
            type="textarea"
            :autosize="{ minRows: 3, maxRows: 6 }"
            placeholder="请输入备注信息"
          />
        </FormItem>
      </Form>

      <div slot="footer">
        <Button @click="handleCancel">取消</Button>
        <Button type="primary" :loading="submitLoading" @click="handleSubmit">确定</Button>
      </div>
    </Modal>
  </div>
</template>

<script>
import {
  getUpdatePackageList,
  createUpdatePackage,
  editUpdatePackage,
  deleteUpdatePackage,
  uploadUpdatePackage,
  batchUpdatePackage
} from '@/api/update-package'
import { getsysparameterList } from '@/api/system/sys_parameter'

export default {
  name: 'UpdatePackage',

  data () {
    return {
      loading: false,
      tableData: [],
      total: 0,
      deviceTypeOptions: [],

      searchParams: {
        keyword: ''
      },
      pageParams: {
        page: 1,
        limit: 10
      },

      modalVisible: false,
      modalTitle: '',
      submitLoading: false,
      uploadLoading: false,

      formData: {
        id: null,
        name: '',
        version: '',
        device_type: 0,
        status: 1,
        package_name: '',
        file_url: '',
        file_size: 0,
        remark: ''
      },

      formRules: {
        name: [
          { required: true, message: '请输入名称', trigger: 'blur' },
          { max: 100, message: '长度不能超过100个字符', trigger: 'blur' }
        ],
        version: [
          { required: true, message: '请输入版本号', trigger: 'blur' },
          { max: 50, message: '长度不能超过50个字符', trigger: 'blur' }
        ],
        device_type: [
          { required: true, message: '请选择设备类型', trigger: 'change' }
        ],
        package_name: [
          { required: true, message: '请输入包名', trigger: 'blur' },
          { max: 200, message: '长度不能超过200个字符', trigger: 'blur' }
        ],
        file_url: [
          { max: 500, message: '长度不能超过500个字符', trigger: 'blur' }
        ],
        remark: [
          { max: 500, message: '长度不能超过500个字符', trigger: 'blur' }
        ]
      },

      columns: [
        { title: 'ID', key: 'id', width: 80 },
        { title: '名称', key: 'name', minWidth: 140, tooltip: true },
        { title: '版本', key: 'version', width: 120 , tooltip: true},
        { title: '设备类型', key: 'device_type_CN', width: 120 , tooltip: true},
        { title: '状态', slot: 'status', width: 100 , tooltip: true},
        { title: '包名', key: 'package_name', minWidth: 180, tooltip: true },
        { title: '文件地址', key: 'file_url', minWidth: 200, tooltip: true },
        { title: '文件大小', slot: 'file_size', width: 120 , tooltip: true},
        { title: '操作', slot: 'action', width: 240, fixed: 'right' }
      ]
    }
  },

  mounted () {
    this.getList()
    this.fetchDeviceTypes()
  },

  methods: {
    async fetchDeviceTypes () {
      try {
        const res = await getsysparameterList('', '', '', 1, 100, 'equipment_info')
        if (res.data.code === 0) {
          const list = res.data.data.list || res.data.data || []
          this.deviceTypeOptions = list
            .filter(item => item.field === 'Etype')
            .map(item => ({ value: item.value, label: item.meaning }))
        }
      } catch (e) {
        console.error('获取设备类型失败', e)
      }
    },

    async getList () {
      this.loading = true
      try {
        const res = await getUpdatePackageList({
          page: this.pageParams.page,
          limit: this.pageParams.limit,
          keyword: this.searchParams.keyword
        })
        if (res.data.code === 0) {
          const result = res.data.data || {}
          this.tableData = (result.list || []).map(item => ({
            ...item,
            device_type_CN: this.getDeviceTypeName(item.device_type)
          }))
          this.total = result.total || 0
        } else {
          this.$Message.error(res.data.msg || '获取列表失败')
        }
      } catch (error) {
        this.$Message.error('获取列表失败')
        console.error(error)
      } finally {
        this.loading = false
      }
    },

    handleSearch () {
      this.pageParams.page = 1
      this.getList()
    },

    handlePageChange (page) {
      this.pageParams.page = page
      this.getList()
    },

    handlePageSizeChange (size) {
      this.pageParams.limit = size
      this.pageParams.page = 1
      this.getList()
    },

    handleAdd () {
      this.modalTitle = '新增更新包'
      this.formData = {
        id: null,
        name: '',
        version: '',
        device_type: '',
        status: 1,
        package_name: '',
        file_url: '',
        file_size: 0,
        remark: ''
      }
      this.modalVisible = true
    },

    handleEdit (row) {
      this.modalTitle = '编辑更新包'
      this.formData = {
        id: row.id,
        name: row.name || '',
        version: row.version || '',
        device_type: row.device_type != null ? String(row.device_type) : '',
        status: row.status !== undefined ? row.status : 1,
        package_name: row.package_name || '',
        file_url: row.file_url || '',
        file_size: row.file_size || 0,
        remark: row.remark || ''
      }
      this.modalVisible = true
    },

    handleCancel () {
      this.modalVisible = false
    },

    handleBeforeUpload (file) {
      // Validate file type
      const isApk = file.name.toLowerCase().endsWith('.apk')
      if (!isApk) {
        this.$Message.error('只能上传APK文件')
        return false
      }

      // Validate file size (max 500MB)
      const maxSize = 500 * 1024 * 1024
      if (file.size > maxSize) {
        this.$Message.error('文件大小不能超过500MB')
        return false
      }

      this.doUpload(file)
      return false
    },

    async doUpload (file) {
      this.uploadLoading = true
      try {
        const formData = new FormData()
        formData.append('file', file)
        formData.append('config', 'update-package')

        const res = await uploadUpdatePackage(formData)
        if (res.data.code === 0) {
          const result = res.data.data || {}
          this.formData.file_url = result.file_url || ''
          this.formData.file_size = result.file_size || file.size
          this.formData.package_name = result.filename || this.formData.package_name || file.name
          this.$Message.success('文件上传成功')
        } else {
          this.$Message.error(res.data.msg || '文件上传失败')
        }
      } catch (error) {
        this.$Message.error('文件上传失败')
        console.error(error)
      } finally {
        this.uploadLoading = false
      }
    },

    async handleSubmit () {
      try {
        this.$refs.packageForm.validate(async (valid) => {
          if (!valid) return

          this.submitLoading = true
          try {
            const submitData = { ...this.formData }
            let res
            if (submitData.id) {
              res = await editUpdatePackage(submitData)
            } else {
              res = await createUpdatePackage(submitData)
            }

            if (res.data.code === 0) {
              this.$Message.success(submitData.id ? '编辑成功' : '新增成功')
              this.modalVisible = false
              this.getList()
            } else {
              this.$Message.error(res.data.msg || '操作失败')
            }
          } catch (error) {
            this.$Message.error('操作失败')
            console.error(error)
          } finally {
            this.submitLoading = false
          }
        })
      } catch (error) {
        this.$Message.error('操作失败')
        console.error(error)
      }
    },

    handleDelete (row) {
      this.$Modal.confirm({
        title: '确认删除',
        content: `确定要删除更新包「${row.name}」吗？`,
        onOk: async () => {
          try {
            const res = await deleteUpdatePackage({ id: row.id })
            if (res.data.code === 0) {
              this.$Message.success('删除成功')
              this.getList()
            } else {
              this.$Message.error(res.data.msg || '删除失败')
            }
          } catch (error) {
            this.$Message.error('删除失败')
            console.error(error)
          }
        }
      })
    },

    handleBatchUpdate (row) {
      this.$Modal.confirm({
        title: '确认批量更新',
        content: `确定要对更新包「${row.name}」（版本: ${row.version}）执行批量更新吗？此操作将向所有设备推送该更新包。`,
        onOk: async () => {
          try {
            const res = await batchUpdatePackage({ id: row.id })
            if (res.data.code === 0) {
              this.$Message.success('批量更新任务已提交')
              this.getList()
            } else {
              this.$Message.error(res.data.msg || '批量更新失败')
            }
          } catch (error) {
            this.$Message.error('批量更新失败')
            console.error(error)
          }
        }
      })
    },

    getDeviceTypeName (type) {
      if (type === undefined || type === null || type === '') return '-'
      const opt = this.deviceTypeOptions.find(o => String(o.value) === String(type))
      return opt ? opt.label : type
    },

    formatFileSize (bytes) {
      if (!bytes || bytes === 0) return '0 B'
      const units = ['B', 'KB', 'MB', 'GB', 'TB']
      let unitIndex = 0
      let size = bytes
      while (size >= 1024 && unitIndex < units.length - 1) {
        size /= 1024
        unitIndex++
      }
      return size.toFixed(2) + ' ' + units[unitIndex]
    }
  }
}
</script>

<style scoped>
.update-package-container {
  padding: 10px;
  background: #F5F5F5;
  min-height: 100%;
}

.search-con {
  display: flex;
  align-items: center;
}
</style>
